import React from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid,
  CalendarClock,
  TrendingUp,
  Wallet,
  AlertCircle,
  XCircle,
  Repeat,
  Clock,
  UserX,
  UserPlus,
  Gauge,
} from 'lucide-react';

// Aliased so the framer-motion `motion` import is referenced in plain JS (the
// project's eslint config has no react plugin, so JSX-only usage is not counted).
const MotionDiv = motion.div;

const KpiCards = ({ cardSummary, loading, selectedPeriod }) => {
  const { t } = useTranslation('dashboard');

  const { selectedDoctor } = useSelector((state) => state.doctor);

  const getCurrencySymbol = () => {
    const tz = selectedDoctor?.timeZone?.toLowerCase();
    return tz === 'asia/kolkata' || tz === 'asia/kolkattta' ? '₹' : '€';
  };

  const currency = getCurrencySymbol();
  const data = cardSummary?.data || {};

  const num = (v) => (Number(v) || 0).toLocaleString();
  const noShowCount = Number(data?.noShow?.count) || 0;
  const cancelledCount = Number(data?.cancelled?.count) || 0;

  const iconClass = 'w-4 h-4 sm:w-5 sm:h-5';
  const tiles = [
    {
      id: 'upcoming-bookings',
      label: t('dashboard.upcoming_bookings', 'Upcoming bookings'),
      value: num(data?.upcomingBookings),
      icon: <CalendarClock className={iconClass} />,
    },
    {
      id: 'upcoming-revenue',
      label: t('dashboard.upcoming_revenue', 'Upcoming revenue'),
      value: `${currency}${num(data?.upcomingRevenue)}`,
      icon: <TrendingUp className={iconClass} />,
    },
    {
      id: 'revenue-generated',
      label: t('dashboard.revenue_generated', 'Revenue generated'),
      value: `${currency}${num(data?.revenueGenerated)}`,
      icon: <Wallet className={iconClass} />,
    },
    {
      id: 'no-show-amount',
      label: t('dashboard.no_show_amount', 'No-show amount'),
      value: `${currency}${num(data?.noShow?.amount)}`,
      sub: t('dashboard.appts_count', '{{count}} appts', { count: noShowCount }),
      icon: <AlertCircle className={iconClass} />,
      accent: 'bg-amber-100 text-amber-600',
    },
    {
      id: 'cancelled-amount',
      label: t('dashboard.cancelled_amount', 'Cancelled amount'),
      value: `${currency}${num(data?.cancelled?.amount)}`,
      sub: t('dashboard.appts_count', '{{count}} appts', { count: cancelledCount }),
      icon: <XCircle className={iconClass} />,
      accent: 'bg-gray-100 text-gray-500',
    },
    {
      id: 'regular-patients',
      label: t('dashboard.regular_patients', 'Regular patients'),
      value: `${Number(data?.regularPatientPercent) || 0}%`,
      icon: <Repeat className={iconClass} />,
    },
    {
      id: 'hourly-rate',
      label: t('dashboard.hourly_rate', 'Hourly rate'),
      value: `${currency}${num(data?.hourlyRate)}/h`,
      icon: <Clock className={iconClass} />,
    },
    {
      id: 'inactive-patients',
      label: t('dashboard.inactive_patients', 'Inactive patients'),
      value: num(data?.inactivePatients),
      icon: <UserX className={iconClass} />,
    },
    {
      id: 'new-patients',
      label: t('dashboard.new_patients', 'New patients'),
      value: num(data?.newPatients),
      icon: <UserPlus className={iconClass} />,
    },
    {
      id: 'avg-occupation',
      label: t('dashboard.avg_occupation', 'Avg occupation'),
      value: `${Number(data?.occupationRate) || 0}%`,
      icon: <Gauge className={iconClass} />,
    },
  ];

  return (
    <div
      data-testid="kpi-cards"
      className="bg-white rounded-xl p-4 border border-gray-200/50 shadow-md"
    >
      <h2
        data-testid="kpi-cards-title"
        className="text-base sm:text-lg font-bold text-gray-900 flex items-center mb-4"
      >
        <LayoutGrid className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
        {t('dashboard.key_metrics', 'Key Metrics')}
      </h2>

      {loading ? (
        <div
          data-testid="kpi-cards-skeleton"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
        >
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              data-testid="kpi-tile-skeleton"
              className="bg-white rounded-xl p-4 border border-gray-200/50 shadow-md animate-pulse"
            >
              <div className="rounded-[14px] p-2 bg-gray-200/70 w-9 h-9 mb-3" />
              <div className="h-4 bg-gray-200/70 rounded w-2/3 mb-2" />
              <div className="h-3 bg-gray-200/50 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : (
        <div
          data-testid="kpi-cards-grid"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
        >
          {tiles.map(({ id, label, value, sub, icon, accent }) => (
            <MotionDiv
              key={id}
              data-testid={`kpi-tile-${id}`}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-xl p-4 border border-gray-200/50 shadow-md"
            >
              <div
                className={`rounded-[14px] p-2 w-fit mb-3 ${accent || 'bg-[#4ABDBD]/10 text-[#4ABDBD]'}`}
              >
                {icon}
              </div>
              <p
                data-testid={`kpi-tile-value-${id}`}
                className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 truncate"
                title={value}
              >
                {value}
              </p>
              <p className="text-[0.65rem] sm:text-xs text-gray-600 font-medium">{label}</p>
              {sub ? (
                <p
                  data-testid={`kpi-tile-sub-${id}`}
                  className="text-[0.6rem] sm:text-[0.7rem] text-gray-400 mt-0.5"
                >
                  {sub}
                </p>
              ) : null}
            </MotionDiv>
          ))}
        </div>
      )}
    </div>
  );
};

export default KpiCards;
